export type Language = 'ko' | 'en' | 'zh-CN' | 'zh-TW' | 'ja' | 'id' | 'vi' | 'es' | 'ar' | 'pt' | 'hi' | 'th' | 'de' | 'fr';

type TranslationDict = Record<string, string>;

export const translations: Record<Language, TranslationDict> = {
  ko: {
    // NicknameModal
    'modal.enter_nickname': '닉네임을 입력하여 참여하세요',
    'modal.nickname_placeholder': '닉네임 입력...',
    'modal.join': '참여하기',
    'modal.current_nickname': '현재 닉네임',
    'modal.new_nickname': '새 닉네임',
    'modal.cancel': '취소',
    'modal.confirm': '확인',
    'modal.edit_message': '메시지 수정',
    'modal.save': '저장',
    'modal.delete_confirm': '정말 이 메시지를 삭제하시겠습니까?',
    'modal.delete': '삭제',

    // ChatInput
    'input.placeholder': '메시지를 입력하세요... (Shift+Enter: 줄바꿈)',
    'input.code_hint': '코드 하이라이팅: ```언어명 코드``` 또는 3줄 이상 입력',
    'input.send': '전송',

    // UserList
    'users.title': '접속자',
    'users.change': '변경',
    'users.me': '나',

    // Header
    'header.settings': '설정',
    'header.connected': '연결됨',
    'header.connecting': '연결 중...',
    'header.disconnected': '연결 안됨',

    // Connecting overlay
    'connecting.waking': '서버를 깨우는 중입니다',
    'connecting.cold_start': 'Render 무료 플랜 콜드 스타트로 최대 30초 걸릴 수 있습니다',
    'connecting.auto_start': '연결되면 자동으로 채팅이 시작됩니다',

    // Settings
    'settings.title': '설정',
    'settings.theme': '테마',
    'settings.language': '언어',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',

    // Nickname change
    'nickname.change_prompt': '새 닉네임을 입력하세요:',
    'nickname.change_title': '닉네임 변경',

    // System nickname
    'system.nickname': '시스템',

    // Toast
    'toast.copied': '클립보드에 복사되었습니다',
    'toast.copy_failed': '복사에 실패했습니다',

    // Typing indicator
    'typing.single': '{name}님이 입력 중...',
    'typing.multiple': '{names}님이 입력 중...',
    'typing.others': '{name} 외 {count}명이 입력 중...',

    // Rooms
    'rooms.title': '채널',
    'rooms.general': '일반',
    'rooms.help': '도움',
    'rooms.showcase': '자랑하기',

    // Mobile menu
    'menu.open': '메뉴',

    // Message actions
    'message.edit': '수정',
    'message.delete': '삭제',
    'message.edited': '(수정됨)',
    'message.deleted': '삭제된 메시지',
    'message.edit_prompt': '메시지를 수정하세요:',
    'message.translate': '번역',
    'message.show_original': '원문',
    'message.translated': '(번역됨)',
    'message.translating': '번역 중...',
    'message.translate_error': '번역에 실패했습니다',
  },
  en: {
    'modal.enter_nickname': 'Enter a nickname to join',
    'modal.nickname_placeholder': 'Enter nickname...',
    'modal.join': 'Join',
    'modal.current_nickname': 'Current nickname',
    'modal.new_nickname': 'New nickname',
    'modal.cancel': 'Cancel',
    'modal.confirm': 'Confirm',
    'modal.edit_message': 'Edit message',
    'modal.save': 'Save',
    'modal.delete_confirm': 'Are you sure you want to delete this message?',
    'modal.delete': 'Delete',

    'input.placeholder': 'Type a message... (Shift+Enter for new line)',
    'input.code_hint': 'Code highlighting: ```language code``` or 3+ lines',
    'input.send': 'Send',

    'users.title': 'Users',
    'users.change': 'Change',
    'users.me': 'me',

    'header.settings': 'Settings',
    'header.connected': 'Connected',
    'header.connecting': 'Connecting...',
    'header.disconnected': 'Disconnected',

    'connecting.waking': 'Waking up the server...',
    'connecting.cold_start': 'Render free plan cold start may take up to 30 seconds',
    'connecting.auto_start': 'Chat will start automatically once connected',

    'settings.title': 'Settings',
    'settings.theme': 'Theme',
    'settings.language': 'Language',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',

    'nickname.change_prompt': 'Enter a new nickname:',
    'nickname.change_title': 'Change Nickname',

    'system.nickname': 'System',

    // Toast
    'toast.copied': 'Copied to clipboard',
    'toast.copy_failed': 'Failed to copy',

    // Typing indicator
    'typing.single': '{name} is typing...',
    'typing.multiple': '{names} are typing...',
    'typing.others': '{name} and {count} others are typing...',

    // Rooms
    'rooms.title': 'Channels',
    'rooms.general': 'General',
    'rooms.help': 'Help',
    'rooms.showcase': 'Showcase',

    // Mobile menu
    'menu.open': 'Menu',

    // Message actions
    'message.edit': 'Edit',
    'message.delete': 'Delete',
    'message.edited': '(edited)',
    'message.deleted': 'Message deleted',
    'message.edit_prompt': 'Edit your message:',
    'message.translate': 'Translate',
    'message.show_original': 'Original',
    'message.translated': '(translated)',
    'message.translating': 'Translating...',
    'message.translate_error': 'Translation failed',
  },
  'zh-CN': {
    'modal.enter_nickname': '输入昵称加入聊天',
    'modal.nickname_placeholder': '输入昵称...',
    'modal.join': '加入',
    'modal.current_nickname': '当前昵称',
    'modal.new_nickname': '新昵称',
    'modal.cancel': '取消',
    'modal.confirm': '确认',
    'modal.edit_message': '编辑消息',
    'modal.save': '保存',
    'modal.delete_confirm': '确定要删除此消息吗？',
    'modal.delete': '删除',

    'input.placeholder': '输入消息... (Shift+Enter 换行)',
    'input.code_hint': '代码高亮: ```语言 代码``` 或 3 行以上',
    'input.send': '发送',

    'users.title': '在线用户',
    'users.change': '修改',
    'users.me': '我',

    'header.settings': '设置',
    'header.connected': '已连接',
    'header.connecting': '连接中...',
    'header.disconnected': '未连接',

    'connecting.waking': '正在唤醒服务器...',
    'connecting.cold_start': 'Render 免费计划冷启动最多可能需要 30 秒',
    'connecting.auto_start': '连接后将自动开始聊天',

    'settings.title': '设置',
    'settings.theme': '主题',
    'settings.language': '语言',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',

    'nickname.change_prompt': '请输入新昵称：',
    'nickname.change_title': '修改昵称',

    'system.nickname': '系统',

    // Toast
    'toast.copied': '已复制到剪贴板',
    'toast.copy_failed': '复制失败',

    // Typing indicator
    'typing.single': '{name}正在输入...',
    'typing.multiple': '{names}正在输入...',
    'typing.others': '{name}等{count}人正在输入...',

    // Rooms
    'rooms.title': '频道',
    'rooms.general': '综合',
    'rooms.help': '帮助',
    'rooms.showcase': '展示',

    // Mobile menu
    'menu.open': '菜单',

    // Message actions
    'message.edit': '编辑',
    'message.delete': '删除',
    'message.edited': '(已编辑)',
    'message.deleted': '消息已删除',
    'message.edit_prompt': '编辑您的消息：',
    'message.translate': '翻译',
    'message.show_original': '原文',
    'message.translated': '(已翻译)',
    'message.translating': '翻译中...',
    'message.translate_error': '翻译失败',
  },
  'zh-TW': {
    'modal.enter_nickname': '輸入暱稱加入聊天',
    'modal.nickname_placeholder': '輸入暱稱...',
    'modal.join': '加入',
    'modal.current_nickname': '目前暱稱',
    'modal.new_nickname': '新暱稱',
    'modal.cancel': '取消',
    'modal.confirm': '確認',
    'modal.edit_message': '編輯訊息',
    'modal.save': '儲存',
    'modal.delete_confirm': '確定要刪除此訊息嗎？',
    'modal.delete': '刪除',

    'input.placeholder': '輸入訊息... (Shift+Enter 換行)',
    'input.code_hint': '程式碼高亮: ```語言 程式碼``` 或 3 行以上',
    'input.send': '發送',

    'users.title': '線上用戶',
    'users.change': '修改',
    'users.me': '我',

    'header.settings': '設定',
    'header.connected': '已連線',
    'header.connecting': '連線中...',
    'header.disconnected': '未連線',

    'connecting.waking': '正在喚醒伺服器...',
    'connecting.cold_start': 'Render 免費方案冷啟動最多可能需要 30 秒',
    'connecting.auto_start': '連線後將自動開始聊天',

    'settings.title': '設定',
    'settings.theme': '主題',
    'settings.language': '語言',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',

    'nickname.change_prompt': '請輸入新暱稱：',
    'nickname.change_title': '修改暱稱',

    'system.nickname': '系統',

    // Toast
    'toast.copied': '已複製到剪貼簿',
    'toast.copy_failed': '複製失敗',

    // Typing indicator
    'typing.single': '{name}正在輸入...',
    'typing.multiple': '{names}正在輸入...',
    'typing.others': '{name}等{count}人正在輸入...',

    // Rooms
    'rooms.title': '頻道',
    'rooms.general': '綜合',
    'rooms.help': '說明',
    'rooms.showcase': '展示',

    // Mobile menu
    'menu.open': '選單',

    // Message actions
    'message.edit': '編輯',
    'message.delete': '刪除',
    'message.edited': '(已編輯)',
    'message.deleted': '訊息已刪除',
    'message.edit_prompt': '編輯您的訊息：',
    'message.translate': '翻譯',
    'message.show_original': '原文',
    'message.translated': '(已翻譯)',
    'message.translating': '翻譯中...',
    'message.translate_error': '翻譯失敗',
  },
  ja: {
    // NicknameModal
    'modal.enter_nickname': 'ニックネームを入力して参加',
    'modal.nickname_placeholder': 'ニックネームを入力...',
    'modal.join': '参加する',
    'modal.current_nickname': '現在のニックネーム',
    'modal.new_nickname': '新しいニックネーム',
    'modal.cancel': 'キャンセル',
    'modal.confirm': '確認',
    'modal.edit_message': 'メッセージを編集',
    'modal.save': '保存',
    'modal.delete_confirm': 'このメッセージを削除してもよろしいですか？',
    'modal.delete': '削除',

    // ChatInput
    'input.placeholder': 'メッセージを入力... (Shift+Enter: 改行)',
    'input.code_hint': 'コードハイライト: ```言語名 コード``` または3行以上',
    'input.send': '送信',

    // UserList
    'users.title': 'オンライン',
    'users.change': '変更',
    'users.me': '自分',

    // Header
    'header.settings': '設定',
    'header.connected': '接続済み',
    'header.connecting': '接続中...',
    'header.disconnected': '未接続',

    // Connecting overlay
    'connecting.waking': 'サーバーを起動しています...',
    'connecting.cold_start': 'Render無料プランのコールドスタートは最大30秒かかる場合があります',
    'connecting.auto_start': '接続されると自動的にチャットが開始されます',

    // Settings
    'settings.title': '設定',
    'settings.theme': 'テーマ',
    'settings.language': '言語',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',

    // Nickname change
    'nickname.change_prompt': '新しいニックネームを入力してください：',
    'nickname.change_title': 'ニックネーム変更',

    // System nickname
    'system.nickname': 'システム',

    // Toast
    'toast.copied': 'クリップボードにコピーしました',
    'toast.copy_failed': 'コピーに失敗しました',

    // Typing indicator
    'typing.single': '{name}さんが入力中...',
    'typing.multiple': '{names}さんが入力中...',
    'typing.others': '{name}さんと他{count}人が入力中...',

    // Rooms
    'rooms.title': 'チャンネル',
    'rooms.general': '一般',
    'rooms.help': 'ヘルプ',
    'rooms.showcase': 'ショーケース',

    // Mobile menu
    'menu.open': 'メニュー',

    // Message actions
    'message.edit': '編集',
    'message.delete': '削除',
    'message.edited': '(編集済み)',
    'message.deleted': 'メッセージが削除されました',
    'message.edit_prompt': 'メッセージを編集してください：',
    'message.translate': '翻訳',
    'message.show_original': '原文',
    'message.translated': '(翻訳済み)',
    'message.translating': '翻訳中...',
    'message.translate_error': '翻訳に失敗しました',
  },
  id: {
    // NicknameModal
    'modal.enter_nickname': 'Masukkan nama panggilan untuk bergabung',
    'modal.nickname_placeholder': 'Masukkan nama panggilan...',
    'modal.join': 'Gabung',
    'modal.current_nickname': 'Nama panggilan saat ini',
    'modal.new_nickname': 'Nama panggilan baru',
    'modal.cancel': 'Batal',
    'modal.confirm': 'Konfirmasi',
    'modal.edit_message': 'Edit pesan',
    'modal.save': 'Simpan',
    'modal.delete_confirm': 'Apakah Anda yakin ingin menghapus pesan ini?',
    'modal.delete': 'Hapus',

    // ChatInput
    'input.placeholder': 'Ketik pesan... (Shift+Enter untuk baris baru)',
    'input.code_hint': 'Sorotan kode: ```bahasa kode``` atau 3+ baris',
    'input.send': 'Kirim',

    // UserList
    'users.title': 'Pengguna',
    'users.change': 'Ubah',
    'users.me': 'saya',

    // Header
    'header.settings': 'Pengaturan',
    'header.connected': 'Terhubung',
    'header.connecting': 'Menghubungkan...',
    'header.disconnected': 'Terputus',

    // Connecting overlay
    'connecting.waking': 'Membangunkan server...',
    'connecting.cold_start': 'Cold start paket gratis Render mungkin memakan waktu hingga 30 detik',
    'connecting.auto_start': 'Obrolan akan dimulai secara otomatis setelah terhubung',

    // Settings
    'settings.title': 'Pengaturan',
    'settings.theme': 'Tema',
    'settings.language': 'Bahasa',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',

    // Nickname change
    'nickname.change_prompt': 'Masukkan nama panggilan baru:',
    'nickname.change_title': 'Ubah Nama Panggilan',

    // System nickname
    'system.nickname': 'Sistem',

    // Toast
    'toast.copied': 'Disalin ke papan klip',
    'toast.copy_failed': 'Gagal menyalin',

    // Typing indicator
    'typing.single': '{name} sedang mengetik...',
    'typing.multiple': '{names} sedang mengetik...',
    'typing.others': '{name} dan {count} lainnya sedang mengetik...',

    // Rooms
    'rooms.title': 'Saluran',
    'rooms.general': 'Umum',
    'rooms.help': 'Bantuan',
    'rooms.showcase': 'Pameran',

    // Mobile menu
    'menu.open': 'Menu',

    // Message actions
    'message.edit': 'Edit',
    'message.delete': 'Hapus',
    'message.edited': '(diedit)',
    'message.deleted': 'Pesan dihapus',
    'message.edit_prompt': 'Edit pesan Anda:',
    'message.translate': 'Terjemahkan',
    'message.show_original': 'Asli',
    'message.translated': '(diterjemahkan)',
    'message.translating': 'Menerjemahkan...',
    'message.translate_error': 'Terjemahan gagal',
  },
  vi: {
    // NicknameModal
    'modal.enter_nickname': 'Nhập biệt danh để tham gia',
    'modal.nickname_placeholder': 'Nhập biệt danh...',
    'modal.join': 'Tham gia',
    'modal.current_nickname': 'Biệt danh hiện tại',
    'modal.new_nickname': 'Biệt danh mới',
    'modal.cancel': 'Hủy',
    'modal.confirm': 'Xác nhận',
    'modal.edit_message': 'Chỉnh sửa tin nhắn',
    'modal.save': 'Lưu',
    'modal.delete_confirm': 'Bạn có chắc chắn muốn xóa tin nhắn này không?',
    'modal.delete': 'Xóa',

    // ChatInput
    'input.placeholder': 'Nhập tin nhắn... (Shift+Enter: xuống dòng)',
    'input.code_hint': 'Tô sáng mã: ```ngôn ngữ mã``` hoặc 3+ dòng',
    'input.send': 'Gửi',

    // UserList
    'users.title': 'Người dùng',
    'users.change': 'Thay đổi',
    'users.me': 'tôi',

    // Header
    'header.settings': 'Cài đặt',
    'header.connected': 'Đã kết nối',
    'header.connecting': 'Đang kết nối...',
    'header.disconnected': 'Đã ngắt kết nối',

    // Connecting overlay
    'connecting.waking': 'Đang khởi động máy chủ...',
    'connecting.cold_start': 'Khởi động lạnh gói miễn phí Render có thể mất đến 30 giây',
    'connecting.auto_start': 'Trò chuyện sẽ tự động bắt đầu khi kết nối',

    // Settings
    'settings.title': 'Cài đặt',
    'settings.theme': 'Giao diện',
    'settings.language': 'Ngôn ngữ',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',

    // Nickname change
    'nickname.change_prompt': 'Nhập biệt danh mới:',
    'nickname.change_title': 'Thay đổi biệt danh',

    // System nickname
    'system.nickname': 'Hệ thống',

    // Toast
    'toast.copied': 'Đã sao chép vào clipboard',
    'toast.copy_failed': 'Sao chép thất bại',

    // Typing indicator
    'typing.single': '{name} đang nhập...',
    'typing.multiple': '{names} đang nhập...',
    'typing.others': '{name} và {count} người khác đang nhập...',

    // Rooms
    'rooms.title': 'Kênh',
    'rooms.general': 'Chung',
    'rooms.help': 'Trợ giúp',
    'rooms.showcase': 'Trưng bày',

    // Mobile menu
    'menu.open': 'Menu',

    // Message actions
    'message.edit': 'Sửa',
    'message.delete': 'Xóa',
    'message.edited': '(đã sửa)',
    'message.deleted': 'Tin nhắn đã bị xóa',
    'message.edit_prompt': 'Sửa tin nhắn của bạn:',
    'message.translate': 'Dịch',
    'message.show_original': 'Gốc',
    'message.translated': '(đã dịch)',
    'message.translating': 'Đang dịch...',
    'message.translate_error': 'Dịch thất bại',
  },
  es: {
    // NicknameModal
    'modal.enter_nickname': 'Ingresa un apodo para unirte',
    'modal.nickname_placeholder': 'Ingresa tu apodo...',
    'modal.join': 'Unirse',
    'modal.current_nickname': 'Apodo actual',
    'modal.new_nickname': 'Nuevo apodo',
    'modal.cancel': 'Cancelar',
    'modal.confirm': 'Confirmar',
    'modal.edit_message': 'Editar mensaje',
    'modal.save': 'Guardar',
    'modal.delete_confirm': '¿Estás seguro de que deseas eliminar este mensaje?',
    'modal.delete': 'Eliminar',

    // ChatInput
    'input.placeholder': 'Escribe un mensaje... (Shift+Enter para nueva línea)',
    'input.code_hint': 'Resaltado de código: ```idioma código``` o 3+ líneas',
    'input.send': 'Enviar',

    // UserList
    'users.title': 'Usuarios',
    'users.change': 'Cambiar',
    'users.me': 'yo',

    // Header
    'header.settings': 'Configuración',
    'header.connected': 'Conectado',
    'header.connecting': 'Conectando...',
    'header.disconnected': 'Desconectado',

    // Connecting overlay
    'connecting.waking': 'Despertando el servidor...',
    'connecting.cold_start': 'El inicio en frío del plan gratuito de Render puede tardar hasta 30 segundos',
    'connecting.auto_start': 'El chat comenzará automáticamente una vez conectado',

    // Settings
    'settings.title': 'Configuración',
    'settings.theme': 'Tema',
    'settings.language': 'Idioma',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',

    // Nickname change
    'nickname.change_prompt': 'Ingresa un nuevo apodo:',
    'nickname.change_title': 'Cambiar apodo',

    // System nickname
    'system.nickname': 'Sistema',

    // Toast
    'toast.copied': 'Copiado al portapapeles',
    'toast.copy_failed': 'Error al copiar',

    // Typing indicator
    'typing.single': '{name} está escribiendo...',
    'typing.multiple': '{names} están escribiendo...',
    'typing.others': '{name} y {count} más están escribiendo...',

    // Rooms
    'rooms.title': 'Canales',
    'rooms.general': 'General',
    'rooms.help': 'Ayuda',
    'rooms.showcase': 'Showcase',

    // Mobile menu
    'menu.open': 'Menú',

    // Message actions
    'message.edit': 'Editar',
    'message.delete': 'Eliminar',
    'message.edited': '(editado)',
    'message.deleted': 'Mensaje eliminado',
    'message.edit_prompt': 'Edita tu mensaje:',
    'message.translate': 'Traducir',
    'message.show_original': 'Original',
    'message.translated': '(traducido)',
    'message.translating': 'Traduciendo...',
    'message.translate_error': 'Error de traducción',
  },
  ar: {
    'modal.enter_nickname': 'أدخل اسمًا مستعارًا للانضمام',
    'modal.nickname_placeholder': 'أدخل اسمًا مستعارًا...',
    'modal.join': 'انضمام',
    'modal.current_nickname': 'الاسم المستعار الحالي',
    'modal.new_nickname': 'اسم مستعار جديد',
    'modal.cancel': 'إلغاء',
    'modal.confirm': 'تأكيد',
    'modal.edit_message': 'تعديل الرسالة',
    'modal.save': 'حفظ',
    'modal.delete_confirm': 'هل أنت متأكد من أنك تريد حذف هذه الرسالة؟',
    'modal.delete': 'حذف',
    'input.placeholder': 'اكتب رسالة... (Shift+Enter لسطر جديد)',
    'input.code_hint': 'إبراز الكود: ```لغة كود``` أو 3+ أسطر',
    'input.send': 'إرسال',
    'users.title': 'المستخدمون',
    'users.change': 'تغيير',
    'users.me': 'أنا',
    'header.settings': 'الإعدادات',
    'header.connected': 'متصل',
    'header.connecting': 'جارٍ الاتصال...',
    'header.disconnected': 'غير متصل',
    'connecting.waking': 'إيقاظ الخادم...',
    'connecting.cold_start': 'قد يستغرق البدء البارد للخطة المجانية من Render حتى 30 ثانية',
    'connecting.auto_start': 'سيبدأ الدردشة تلقائيًا بعد الاتصال',
    'settings.title': 'الإعدادات',
    'settings.theme': 'المظهر',
    'settings.language': 'اللغة',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',
    'nickname.change_prompt': 'أدخل اسمًا مستعارًا جديدًا:',
    'nickname.change_title': 'تغيير الاسم المستعار',
    'system.nickname': 'النظام',
    'toast.copied': 'تم النسخ إلى الحافظة',
    'toast.copy_failed': 'فشل النسخ',
    'typing.single': '{name} يكتب...',
    'typing.multiple': '{names} يكتبون...',
    'typing.others': '{name} و {count} آخرون يكتبون...',
    'rooms.title': 'القنوات',
    'rooms.general': 'عام',
    'rooms.help': 'مساعدة',
    'rooms.showcase': 'معرض',
    'menu.open': 'القائمة',
    'message.edit': 'تعديل',
    'message.delete': 'حذف',
    'message.edited': '(تم التعديل)',
    'message.deleted': 'تم حذف الرسالة',
    'message.edit_prompt': 'عدّل رسالتك:',
    'message.translate': 'ترجمة',
    'message.show_original': 'الأصلي',
    'message.translated': '(مترجم)',
    'message.translating': 'جارٍ الترجمة...',
    'message.translate_error': 'فشلت الترجمة',
  },
  pt: {
    'modal.enter_nickname': 'Digite um apelido para entrar',
    'modal.nickname_placeholder': 'Digite seu apelido...',
    'modal.join': 'Entrar',
    'modal.current_nickname': 'Apelido atual',
    'modal.new_nickname': 'Novo apelido',
    'modal.cancel': 'Cancelar',
    'modal.confirm': 'Confirmar',
    'modal.edit_message': 'Editar mensagem',
    'modal.save': 'Salvar',
    'modal.delete_confirm': 'Tem certeza de que deseja excluir esta mensagem?',
    'modal.delete': 'Excluir',
    'input.placeholder': 'Digite uma mensagem... (Shift+Enter para nova linha)',
    'input.code_hint': 'Destaque de código: ```idioma código``` ou 3+ linhas',
    'input.send': 'Enviar',
    'users.title': 'Usuários',
    'users.change': 'Alterar',
    'users.me': 'eu',
    'header.settings': 'Configurações',
    'header.connected': 'Conectado',
    'header.connecting': 'Conectando...',
    'header.disconnected': 'Desconectado',
    'connecting.waking': 'Acordando o servidor...',
    'connecting.cold_start': 'A inicialização a frio do plano gratuito do Render pode levar até 30 segundos',
    'connecting.auto_start': 'O chat começará automaticamente após a conexão',
    'settings.title': 'Configurações',
    'settings.theme': 'Tema',
    'settings.language': 'Idioma',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',
    'nickname.change_prompt': 'Digite um novo apelido:',
    'nickname.change_title': 'Alterar Apelido',
    'system.nickname': 'Sistema',
    'toast.copied': 'Copiado para a área de transferência',
    'toast.copy_failed': 'Falha ao copiar',
    'typing.single': '{name} está digitando...',
    'typing.multiple': '{names} estão digitando...',
    'typing.others': '{name} e mais {count} estão digitando...',
    'rooms.title': 'Canais',
    'rooms.general': 'Geral',
    'rooms.help': 'Ajuda',
    'rooms.showcase': 'Showcase',
    'menu.open': 'Menu',
    'message.edit': 'Editar',
    'message.delete': 'Excluir',
    'message.edited': '(editado)',
    'message.deleted': 'Mensagem excluída',
    'message.edit_prompt': 'Edite sua mensagem:',
    'message.translate': 'Traduzir',
    'message.show_original': 'Original',
    'message.translated': '(traduzido)',
    'message.translating': 'Traduzindo...',
    'message.translate_error': 'Falha na tradução',
  },
  hi: {
    'modal.enter_nickname': 'शामिल होने के लिए एक उपनाम दर्ज करें',
    'modal.nickname_placeholder': 'उपनाम दर्ज करें...',
    'modal.join': 'शामिल हों',
    'modal.current_nickname': 'वर्तमान उपनाम',
    'modal.new_nickname': 'नया उपनाम',
    'modal.cancel': 'रद्द करें',
    'modal.confirm': 'पुष्टि करें',
    'modal.edit_message': 'संदेश संपादित करें',
    'modal.save': 'सहेजें',
    'modal.delete_confirm': 'क्या आप वाकई इस संदेश को हटाना चाहते हैं?',
    'modal.delete': 'हटाएं',
    'input.placeholder': 'संदेश टाइप करें... (Shift+Enter नई पंक्ति के लिए)',
    'input.code_hint': 'कोड हाइलाइटिंग: ```भाषा कोड``` या 3+ पंक्तियाँ',
    'input.send': 'भेजें',
    'users.title': 'उपयोगकर्ता',
    'users.change': 'बदलें',
    'users.me': 'मैं',
    'header.settings': 'सेटिंग्स',
    'header.connected': 'कनेक्टेड',
    'header.connecting': 'कनेक्ट हो रहा है...',
    'header.disconnected': 'डिस्कनेक्टेड',
    'connecting.waking': 'सर्वर को जगा रहे हैं...',
    'connecting.cold_start': 'Render मुफ्त योजना कोल्ड स्टार्ट में 30 सेकंड तक का समय लग सकता है',
    'connecting.auto_start': 'कनेक्ट होने के बाद चैट स्वचालित रूप से शुरू हो जाएगी',
    'settings.title': 'सेटिंग्स',
    'settings.theme': 'थीम',
    'settings.language': 'भाषा',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',
    'nickname.change_prompt': 'नया उपनाम दर्ज करें:',
    'nickname.change_title': 'उपनाम बदलें',
    'system.nickname': 'सिस्टम',
    'toast.copied': 'क्लिपबोर्ड पर कॉपी किया गया',
    'toast.copy_failed': 'कॉपी करने में विफल',
    'typing.single': '{name} टाइप कर रहे हैं...',
    'typing.multiple': '{names} टाइप कर रहे हैं...',
    'typing.others': '{name} और {count} अन्य टाइप कर रहे हैं...',
    'rooms.title': 'चैनल',
    'rooms.general': 'सामान्य',
    'rooms.help': 'सहायता',
    'rooms.showcase': 'प्रदर्शन',
    'menu.open': 'मेनू',
    'message.edit': 'संपादित करें',
    'message.delete': 'हटाएं',
    'message.edited': '(संपादित)',
    'message.deleted': 'संदेश हटाया गया',
    'message.edit_prompt': 'अपना संदेश संपादित करें:',
    'message.translate': 'अनुवाद करें',
    'message.show_original': 'मूल',
    'message.translated': '(अनुवादित)',
    'message.translating': 'अनुवाद हो रहा है...',
    'message.translate_error': 'अनुवाद विफल',
  },
  th: {
    'modal.enter_nickname': 'ใส่ชื่อเล่นเพื่อเข้าร่วม',
    'modal.nickname_placeholder': 'ใส่ชื่อเล่น...',
    'modal.join': 'เข้าร่วม',
    'modal.current_nickname': 'ชื่อเล่นปัจจุบัน',
    'modal.new_nickname': 'ชื่อเล่นใหม่',
    'modal.cancel': 'ยกเลิก',
    'modal.confirm': 'ยืนยัน',
    'modal.edit_message': 'แก้ไขข้อความ',
    'modal.save': 'บันทึก',
    'modal.delete_confirm': 'คุณแน่ใจหรือไม่ที่จะลบข้อความนี้?',
    'modal.delete': 'ลบ',
    'input.placeholder': 'พิมพ์ข้อความ... (Shift+Enter สำหรับบรรทัดใหม่)',
    'input.code_hint': 'ไฮไลต์โค้ด: ```ภาษา โค้ด``` หรือ 3+ บรรทัด',
    'input.send': 'ส่ง',
    'users.title': 'ผู้ใช้',
    'users.change': 'เปลี่ยน',
    'users.me': 'ฉัน',
    'header.settings': 'การตั้งค่า',
    'header.connected': 'เชื่อมต่อแล้ว',
    'header.connecting': 'กำลังเชื่อมต่อ...',
    'header.disconnected': 'ไม่ได้เชื่อมต่อ',
    'connecting.waking': 'กำลังปลุกเซิร์ฟเวอร์...',
    'connecting.cold_start': 'การเริ่มต้นเย็นของแผนฟรี Render อาจใช้เวลาถึง 30 วินาที',
    'connecting.auto_start': 'แชทจะเริ่มอัตโนมัติเมื่อเชื่อมต่อแล้ว',
    'settings.title': 'การตั้งค่า',
    'settings.theme': 'ธีม',
    'settings.language': 'ภาษา',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',
    'nickname.change_prompt': 'ใส่ชื่อเล่นใหม่:',
    'nickname.change_title': 'เปลี่ยนชื่อเล่น',
    'system.nickname': 'ระบบ',
    'toast.copied': 'คัดลอกไปยังคลิปบอร์ดแล้ว',
    'toast.copy_failed': 'คัดลอกล้มเหลว',
    'typing.single': '{name} กำลังพิมพ์...',
    'typing.multiple': '{names} กำลังพิมพ์...',
    'typing.others': '{name} และอีก {count} คนกำลังพิมพ์...',
    'rooms.title': 'ช่อง',
    'rooms.general': 'ทั่วไป',
    'rooms.help': 'ช่วยเหลือ',
    'rooms.showcase': 'นิทรรศการ',
    'menu.open': 'เมนู',
    'message.edit': 'แก้ไข',
    'message.delete': 'ลบ',
    'message.edited': '(แก้ไขแล้ว)',
    'message.deleted': 'ลบข้อความแล้ว',
    'message.edit_prompt': 'แก้ไขข้อความของคุณ:',
    'message.translate': 'แปล',
    'message.show_original': 'ต้นฉบับ',
    'message.translated': '(แปลแล้ว)',
    'message.translating': 'กำลังแปล...',
    'message.translate_error': 'การแปลล้มเหลว',
  },
  de: {
    'modal.enter_nickname': 'Gib einen Spitznamen ein, um beizutreten',
    'modal.nickname_placeholder': 'Spitzname eingeben...',
    'modal.join': 'Beitreten',
    'modal.current_nickname': 'Aktueller Spitzname',
    'modal.new_nickname': 'Neuer Spitzname',
    'modal.cancel': 'Abbrechen',
    'modal.confirm': 'Bestätigen',
    'modal.edit_message': 'Nachricht bearbeiten',
    'modal.save': 'Speichern',
    'modal.delete_confirm': 'Möchtest du diese Nachricht wirklich löschen?',
    'modal.delete': 'Löschen',
    'input.placeholder': 'Nachricht eingeben... (Shift+Enter für neue Zeile)',
    'input.code_hint': 'Code-Hervorhebung: ```Sprache Code``` oder 3+ Zeilen',
    'input.send': 'Senden',
    'users.title': 'Benutzer',
    'users.change': 'Ändern',
    'users.me': 'Ich',
    'header.settings': 'Einstellungen',
    'header.connected': 'Verbunden',
    'header.connecting': 'Verbinde...',
    'header.disconnected': 'Getrennt',
    'connecting.waking': 'Server wird aufgeweckt...',
    'connecting.cold_start': 'Der Kaltstart des kostenlosen Render-Plans kann bis zu 30 Sekunden dauern',
    'connecting.auto_start': 'Der Chat startet automatisch nach dem Verbinden',
    'settings.title': 'Einstellungen',
    'settings.theme': 'Design',
    'settings.language': 'Sprache',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',
    'nickname.change_prompt': 'Neuen Spitznamen eingeben:',
    'nickname.change_title': 'Spitznamen ändern',
    'system.nickname': 'System',
    'toast.copied': 'In die Zwischenablage kopiert',
    'toast.copy_failed': 'Kopieren fehlgeschlagen',
    'typing.single': '{name} tippt...',
    'typing.multiple': '{names} tippen...',
    'typing.others': '{name} und {count} weitere tippen...',
    'rooms.title': 'Kanäle',
    'rooms.general': 'Allgemein',
    'rooms.help': 'Hilfe',
    'rooms.showcase': 'Showcase',
    'menu.open': 'Menü',
    'message.edit': 'Bearbeiten',
    'message.delete': 'Löschen',
    'message.edited': '(bearbeitet)',
    'message.deleted': 'Nachricht gelöscht',
    'message.edit_prompt': 'Nachricht bearbeiten:',
    'message.translate': 'Übersetzen',
    'message.show_original': 'Original',
    'message.translated': '(übersetzt)',
    'message.translating': 'Übersetze...',
    'message.translate_error': 'Übersetzung fehlgeschlagen',
  },
  fr: {
    'modal.enter_nickname': 'Entrez un pseudo pour rejoindre',
    'modal.nickname_placeholder': 'Entrez un pseudo...',
    'modal.join': 'Rejoindre',
    'modal.current_nickname': 'Pseudo actuel',
    'modal.new_nickname': 'Nouveau pseudo',
    'modal.cancel': 'Annuler',
    'modal.confirm': 'Confirmer',
    'modal.edit_message': 'Modifier le message',
    'modal.save': 'Enregistrer',
    'modal.delete_confirm': 'Es-tu sûr de vouloir supprimer ce message?',
    'modal.delete': 'Supprimer',
    'input.placeholder': 'Tape un message... (Shift+Enter pour nouvelle ligne)',
    'input.code_hint': 'Coloration syntaxique: ```langue code``` ou 3+ lignes',
    'input.send': 'Envoyer',
    'users.title': 'Utilisateurs',
    'users.change': 'Changer',
    'users.me': 'moi',
    'header.settings': 'Paramètres',
    'header.connected': 'Connecté',
    'header.connecting': 'Connexion...',
    'header.disconnected': 'Déconnecté',
    'connecting.waking': 'Réveil du serveur...',
    'connecting.cold_start': 'Le démarrage à froid du plan gratuit Render peut prendre jusqu\'à 30 secondes',
    'connecting.auto_start': 'Le chat démarrera automatiquement une fois connecté',
    'settings.title': 'Paramètres',
    'settings.theme': 'Thème',
    'settings.language': 'Langue',
    'settings.lang_ko': '한국어',
    'settings.lang_en': 'English',
    'settings.lang_zh-CN': '简体中文',
    'settings.lang_zh-TW': '繁體中文',
    'settings.lang_ja': '日本語',
    'settings.lang_id': 'Bahasa Indonesia',
    'settings.lang_vi': 'Tiếng Việt',
    'settings.lang_es': 'Español',
    'settings.lang_ar': 'العربية',
    'settings.lang_pt': 'Português',
    'settings.lang_hi': 'हिन्दी',
    'settings.lang_th': 'ไทย',
    'settings.lang_de': 'Deutsch',
    'settings.lang_fr': 'Français',
    'nickname.change_prompt': 'Entrez un nouveau pseudo:',
    'nickname.change_title': 'Changer de pseudo',
    'system.nickname': 'Système',
    'toast.copied': 'Copié dans le presse-papiers',
    'toast.copy_failed': 'Échec de la copie',
    'typing.single': '{name} écrit...',
    'typing.multiple': '{names} écrivent...',
    'typing.others': '{name} et {count} autres écrivent...',
    'rooms.title': 'Salons',
    'rooms.general': 'Général',
    'rooms.help': 'Aide',
    'rooms.showcase': 'Showcase',
    'menu.open': 'Menu',
    'message.edit': 'Modifier',
    'message.delete': 'Supprimer',
    'message.edited': '(modifié)',
    'message.deleted': 'Message supprimé',
    'message.edit_prompt': 'Modifie ton message:',
    'message.translate': 'Traduire',
    'message.show_original': 'Original',
    'message.translated': '(traduit)',
    'message.translating': 'Traduction...',
    'message.translate_error': 'Échec de la traduction',
  },
};
